import React, { useContext, useEffect, useState } from 'react'
import { css } from '@linaria/core'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { VisuallyHidden } from '@reach/visually-hidden'
import { WhiteThemeContext } from '@fs/zion-ui/quarks-authorized-use-only'

import { boolAttr } from '@fs/zion-frontend-friends'
import { i18n, isTranslationAvailable } from '@fs/zion-locale'
import { Link } from '@fs/zion-router'
import { Row } from '@fs/zion-ui'

import getMobileAppInfo from './getMobileAppInfo'

const linkCss = css`
  max-width: 100%;
  display: block;
  background-size: contain;
`

const androidLinkCss = css`
  aspect-ratio: 135 / 40;
  width: 135px;
  height: 42px;
`

const iosLinkCss = css`
  aspect-ratio: 119.7 / 40;
  width: 119.7px;
  height: 40px;
`

const stackBtnsCss = css`
  width: 278px;
`

function MobileAppButtonBase({
  image: Image,
  widthOverride,
  link,
  accessibilityText,
  linkName,
  buttonClass,
  newTab,
  light,
}) {
  if (widthOverride) {
    Image.width = `${widthOverride}px`
    Image.aspectRatio = `${widthOverride} / 40`
  }
  const isWhite = useContext(WhiteThemeContext)

  return (
    <Link
      to={link}
      external
      noLinkStyles
      linkName={linkName}
      target={newTab ? '_blank' : undefined}
      className={`${linkCss} ${buttonClass}`}
      data-light={boolAttr(light)}
      data-white={boolAttr(isWhite)}
    >
      <Image />
      <VisuallyHidden>{accessibilityText}</VisuallyHidden>
    </Link>
  )
}

function MultipleButtonsBase({ centered, children, stacked }) {
  return (
    <Row
      className={stacked ? stackBtnsCss : null}
      alignX={centered ? 'center' : 'start'}
      gutters="xs"
      wrap
      alignY="middle"
    >
      {children}
    </Row>
  )
}

function AndroidButton({ link, newTab, light, accessibilityLabel }) {
  const [t] = useTranslation()
  const [mobileProps, setMobileProps] = useState(null)

  useEffect(() => {
    let mounted = true
    getMobileAppInfo(i18n.language, 'android')
      .then((props) => {
        mounted && setMobileProps(props)
      })
      .catch((error) => {
        console.error(error)
      })
    return () => {
      mounted = false
    }
  }, [])

  if (!mobileProps) {
    return null
  }

  return (
    <MobileAppButtonBase
      {...mobileProps}
      accessibilityText={accessibilityLabel || t('mobile-app-buttons.google-play', 'Get it on Google Play')}
      linkName="FamilySearch Google Play link"
      link={link}
      buttonClass={androidLinkCss}
      newTab={newTab}
      light={light}
    />
  )
}

function IosButton({ link, newTab, light, accessibilityLabel }) {
  const [t] = useTranslation()
  const [mobileProps, setMobileProps] = useState(null)

  useEffect(() => {
    let mounted = true
    getMobileAppInfo(i18n.language, 'ios')
      .then((props) => {
        mounted && setMobileProps(props)
      })
      .catch((error) => {
        console.error(error)
      })
    return () => {
      mounted = false
    }
  }, [])

  if (!mobileProps) {
    return null
  }

  return (
    <MobileAppButtonBase
      {...mobileProps}
      accessibilityText={accessibilityLabel || t('mobile-app-buttons.app-store', 'Download on the App Store')}
      linkName="FamilySearch App Store link"
      link={link}
      buttonClass={iosLinkCss}
      newTab={newTab}
      light={light}
    />
  )
}

// ---  FamilySearch Tree App ---

export function TreeAppAndroidButton({ newTab, light }) {
  return (
    <AndroidButton
      newTab={newTab}
      link="https://play.google.com/store/apps/details?id=org.familysearch.mobile"
      light={light}
    />
  )
}

export function TreeAppIosButton({ newTab, light }) {
  return <IosButton newTab={newTab} link="https://apps.apple.com/app/familysearch-tree/id885982973" light={light} />
}

export function TreeAppButtons({ centered, newTab, light, stacked }) {
  return (
    <MultipleButtonsBase centered={centered} stacked={stacked}>
      <TreeAppAndroidButton newTab={newTab} light={light} />
      <TreeAppIosButton newTab={newTab} light={light} />
    </MultipleButtonsBase>
  )
}

// ---  Memories App ---
export function MemoriesAppAndroidButton({ newTab, light }) {
  const [t] = useTranslation()

  return (
    <AndroidButton
      newTab={newTab}
      link="https://play.google.com/store/apps/details?id=org.familysearch.mobile.memories"
      light={light}
      accessibilityLabel={
        isTranslationAvailable('mobile-app-buttons.memories.google-play')
          ? t('mobile-app-buttons.memories.google-play', 'Get the Memories App on Google Play')
          : t('mobile-app-buttons.google-play', 'Get it on Google Play')
      }
    />
  )
}

export function MemoriesAppIosButton({ newTab, light }) {
  const [t] = useTranslation()

  return (
    <IosButton
      newTab={newTab}
      link="https://apps.apple.com/app/familysearch-memories/id885970971"
      light={light}
      accessibilityLabel={
        isTranslationAvailable('mobile-app-buttons.memories.app-store')
          ? t('mobile-app-buttons.memories.app-store', 'Get the Memories App on the App Store')
          : t('mobile-app-buttons.app-store', 'Download on the App Store')
      }
    />
  )
}

export function MemoriesAppButtons({ centered, newTab, light, stacked }) {
  return (
    <MultipleButtonsBase centered={centered} stacked={stacked}>
      <MemoriesAppAndroidButton newTab={newTab} light={light} />
      <MemoriesAppIosButton newTab={newTab} light={light} />
    </MultipleButtonsBase>
  )
}

// --- Together by FamilySearch App ---

export function TogetherAppAndroidButton({ newTab, light }) {
  return (
    <AndroidButton
      newTab={newTab}
      link="https://play.google.com/store/apps/details?id=org.familysearch.www.together_twa"
      light={light}
    />
  )
}

export function TogetherAppIosButton({ newTab, light }) {
  return (
    <IosButton
      newTab={newTab}
      link="https://apps.apple.com/us/app/together-by-familysearch/id1626322239"
      light={light}
    />
  )
}

export function TogetherAppButtons({ centered, newTab, light, stacked }) {
  return (
    <MultipleButtonsBase centered={centered} stacked={stacked}>
      <TogetherAppAndroidButton newTab={newTab} light={light} />
      <TogetherAppIosButton newTab={newTab} light={light} />
    </MultipleButtonsBase>
  )
}
// --- FamilySearch GetInvolved App ---

export function GetInvolvedAppAndroidButton({ newTab, light }) {
  return (
    <AndroidButton
      newTab={newTab}
      link="https://play.google.com/store/apps/details?id=org.familysearch.mobile.idx.review"
      light={light}
    />
  )
}

export function GetInvolvedAppIosButton({ newTab, light }) {
  return (
    <IosButton
      newTab={newTab}
      link="https://apps.apple.com/us/app/familysearch-get-involved/id1567446332"
      light={light}
    />
  )
}

export function GetInvolvedAppButtons({ centered, newTab, light, stacked }) {
  return (
    <MultipleButtonsBase centered={centered} stacked={stacked}>
      <GetInvolvedAppAndroidButton newTab={newTab} light={light} />
      <GetInvolvedAppIosButton newTab={newTab} light={light} />
    </MultipleButtonsBase>
  )
}

// --- All Family Search Apps ---

export function FSAppsAndroidButton({ newTab, light }) {
  return (
    <AndroidButton newTab={newTab} link="https://play.google.com/store/apps/dev?id=8264880723452882397" light={light} />
  )
}

export function FSAppsIosButton({ newTab, light }) {
  return (
    <IosButton
      newTab={newTab}
      link="https://apps.apple.com/us/developer/familysearch-international/id495353625"
      light={light}
    />
  )
}

export function FSAppsButtons({ centered, newTab, light, stacked }) {
  return (
    <MultipleButtonsBase centered={centered} stacked={stacked}>
      <FSAppsAndroidButton newTab={newTab} light={light} />
      <FSAppsIosButton newTab={newTab} light={light} />
    </MultipleButtonsBase>
  )
}

TogetherAppButtons.propTypes = {
  /** Centers the buttons. */
  centered: PropTypes.bool,
  /** Opens the link in a new tab. */
  newTab: PropTypes.bool,
  /** Controls the complementary (light) version of the buttons */
  light: PropTypes.bool,
  /** Stacks buttons rather than keeping them on the same line */
  stacked: PropTypes.bool,
}

TreeAppButtons.propTypes = {
  /** Centers the buttons. */
  centered: PropTypes.bool,
  /** Opens the link in a new tab. */
  newTab: PropTypes.bool,
  /** Controls the complementary (light) version of the buttons */
  light: PropTypes.bool,
  /** Stacks buttons rather than keeping them on the same line */
  stacked: PropTypes.bool,
}

MemoriesAppButtons.propTypes = {
  /** Centers the buttons. */
  centered: PropTypes.bool,
  /** Opens the link in a new tab. */
  newTab: PropTypes.bool,
  /** Controls the complementary (light) version of the buttons */
  light: PropTypes.bool,
  /** Stacks buttons rather than keeping them on the same line */
  stacked: PropTypes.bool,
}

FSAppsButtons.propTypes = {
  /** Centers the buttons. */
  centered: PropTypes.bool,
  /** Opens the link in a new tab. */
  newTab: PropTypes.bool,
  /** Controls the complementary (light) version of the buttons */
  light: PropTypes.bool,
  /** Stacks buttons rather than keeping them on the same line */
  stacked: PropTypes.bool,
}
